import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Share, ArrowLeft } from 'lucide-react';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  className?: string;
}

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showBackButton,
  onBack,
  className = '',
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`bg-app-surface-elevated border-border/30 text-text-primary max-w-md ${className}`}
      >
        <DialogHeader className="relative">
          {showBackButton && (
            <button
              data-test-id="modal-back-btn"
              onClick={onBack}
              className="absolute left-0 top-0 p-2 text-text-secondary hover:text-text-primary transition-smooth"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="ml-2 text-sm">Back</span>
            </button>
          )}

          {title && (
            <DialogTitle className="text-center text-text-primary font-bold text-xl">
              {title}
            </DialogTitle>
          )}
        </DialogHeader>

        <div className="mt-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
};

// League Created Modal
interface LeagueCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  pin: string;
  onViewLeague: () => void;
}

export const LeagueCreatedModal: React.FC<LeagueCreatedModalProps> = ({
  isOpen,
  onClose,
  pin,
  onViewLeague,
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(pin);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Join my DraftKings Pick'Em league",
        text: `Use PIN: ${pin}`,
      });
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="LEAGUE CREATED!"
      showBackButton
      onBack={onClose}
    >
      <div className="text-center space-y-6">
        <p className="text-text-secondary">
          Show this PIN to invite friends to your private league
        </p>

        {/* PIN Display */}
        <div className="flex justify-center gap-2">
          {pin.split('').map((char, index) => (
            <div
              key={index}
              className="w-12 h-12 bg-app-surface rounded-lg flex items-center justify-center text-text-primary font-bold text-xl"
            >
              {char}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            data-test-id="copy-pin-btn"
            variant="secondary"
            onClick={handleCopy}
            className="flex-1 gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy
          </Button>
          <Button
            data-test-id="share-pin-btn"
            variant="pickem"
            onClick={handleShare}
            className="flex-1 gap-2"
          >
            <Share className="w-4 h-4" />
            Share
          </Button>
        </div>

        <p className="text-text-muted text-sm">
          You can always share your code with friends later on the friends leaderboard page
        </p>

        <Button
          data-test-id="view-league-btn"
          variant="pickem"
          onClick={onViewLeague}
          className="w-full"
        >
          View League
        </Button>
      </div>
    </BaseModal>
  );
};

// Picks Submitted Modal
interface PicksSubmittedModalProps {
  isOpen: boolean;
  onClose: () => void;
  potentialWinnings: string;
  onAddToBetSlip: () => void;
  onReturnToLobby: () => void;
}

export const PicksSubmittedModal: React.FC<PicksSubmittedModalProps> = ({
  isOpen,
  onClose,
  potentialWinnings,
  onAddToBetSlip,
  onReturnToLobby,
}) => {
  const { t } = useTranslation();

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={t('modal.picksSubmitted.title')}>
      <div className="text-center space-y-6">
        <p className="text-text-secondary">
          {t('modal.picksSubmitted.message')}
          <br />
          {t('modal.picksSubmitted.editMessage')}
        </p>

        <div className="bg-app-surface rounded-xl p-4">
          <p className="text-text-secondary text-sm mb-2">Like your picks?</p>
          <p className="text-text-secondary text-sm mb-4">
            Build a bet slip with the same selections.
          </p>

          <div className="space-y-2">
            <p className="text-text-primary font-bold text-sm">YOU COULD WIN</p>
            <p className="text-text-primary font-bold text-3xl">{potentialWinnings}</p>
            <p className="text-text-secondary text-sm">from a $10 bet</p>
          </div>
        </div>

        <div className="space-y-3">
          <Button variant="pickem" onClick={onAddToBetSlip} className="w-full">
            Add to Bet Slip
          </Button>
          <Button variant="secondary" onClick={onReturnToLobby} className="w-full">
            {t('modal.picksSubmitted.returnToLobby')}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

// Clear Selections Modal
interface ClearSelectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClearPicks: () => void;
}

export const ClearSelectionsModal: React.FC<ClearSelectionsModalProps> = ({
  isOpen,
  onClose,
  onClearPicks,
}) => {
  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="CLEAR SELECTIONS">
      <div className="text-center space-y-6">
        <p className="text-text-secondary">
          This action clears all current selections.
          <br />
          Are you sure you would like to clear all your picks?
        </p>

        <div className="space-y-3">
          <Button variant="pickem" onClick={onClearPicks} className="w-full">
            Clear Pick
          </Button>
          <Button variant="secondary" onClick={onClose} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default BaseModal;
